import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useRef, useState, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useParams } from 'react-router-dom';
import { resetCurrentFinancialInformation } from '../../actions/current-financial-information';
import {
  getDefaultFinancialInformation,
  resetDefaultFinancialInformation,
} from '../../actions/default-financial-information';
import { clear, find } from '../../actions/item';
import { clearLiveshoppingReference } from '../../actions/liveshopping';
import { clear as clearRetailer, find as getRetailer } from '../../actions/retailer';
import { trackViewEvent } from '../../actions/tracking';
import { RenderClientSide } from '../../components/render';
import SelectedArticle from '../../components/selected-article';
import { events, text } from '../../helpers';
import useImboxWithRetailer from '../../hooks/imbox';
import useTrackPageView from '../../hooks/useTrackPageView';
import { getMarket } from '../../i18n';
import type { RootState } from '../../reducers';
import { getItem, getItemBranch } from '../../selectors/item';
import { getRetailerDocument, getRetailerId } from '../../selectors/retailer';
import Loader from '../../styled-components/components/Loader';
import { Page } from '../../styled-components/components/Page';
import { Separator } from '../../styled-components/components/Separator';
import { getSelectedCentralStorageRetailer, isErrorPage } from '../../utils/reselectors';
import MissingItemView from '../missing-item';
import { resolveArticlesRoute } from '../utils';
import Accessories from './accessories';
import Branch from './branch';
import Card from './card';
import ChargingCost from './charging-cost';
import Documents from './documents';
import Equipment from './equipment';
import FloatingHeader from './floating-header';
import Gallery from './gallery';
import GuaranteePackage from './guarantee-package';
import Information from './information';
import MessageModal from './message-modal';
import Promos from './promos';
import Properties from './properties';
import SafetyData from './safety-data';

const VehicleList = lazy(() => import('../../components/vehicle-list'));
const ArticleList = lazy(() => import('../../components/article-list/ArticleListItem'));

const Item = () => {
  const urlParams = useParams();
  const id = urlParams.id!;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const notFound = useSelector(isErrorPage);
  const item = useSelector(getItem);
  const retailerId = useSelector(getRetailerId);
  const retailer = useSelector(getRetailerDocument);
  const itemBranch = useSelector(getItemBranch);
  const centralStorageRetailer = useSelector(getSelectedCentralStorageRetailer);
  const showMessageModal = useSelector<RootState, boolean>((s) => s.events.showMessageModal);
  const liveshoppingReference = useSelector<RootState, string | undefined>(
    (s) => s.liveshopping.reference,
  );

  const actions = bindActionCreators(
    {
      find,
      clear,
      clearRetailer,
      trackViewEvent,
      getRetailer,
      getDefaultFinancialInformation,
      resetDefaultFinancialInformation,
      resetCurrentFinancialInformation,
      clearLiveshoppingReference,
    },
    dispatch,
  );
  const trackPageViewEvent = useTrackPageView();
  const marketCode = getMarket().marketCode;

  const liveshoppingReferenceRef = useRef(liveshoppingReference);
  liveshoppingReferenceRef.current = liveshoppingReference;

  useImboxWithRetailer(retailerId);
  const [showFloatingHeader, setShowFloatingHeader] = useState(false);

  const contactSectionRef = useRef<HTMLDivElement | null>(null);
  useScrollPosition(({ currPos }) => {
    if (contactSectionRef?.current?.offsetTop) {
      setShowFloatingHeader(Math.abs(currPos.y) > contactSectionRef.current.offsetTop);
    } else {
      setShowFloatingHeader(false);
    }
  });

  const onGetRetailer = () => {
    if (
      (!retailerId && centralStorageRetailer) ||
      (retailerId && centralStorageRetailer && retailerId !== centralStorageRetailer.id)
    ) {
      actions.getRetailer(centralStorageRetailer.id);
    } else if (!retailerId && itemBranch) {
      if (centralStorageRetailer) {
        actions.getRetailer(centralStorageRetailer.id);
      } else {
        actions.getRetailer(itemBranch.id);
      }
    }
  };

  useEffect(() => {
    const isCorrectItem = item && item.id === id;
    if (!isCorrectItem) return;

    if (!retailer) return;

    const hasFinancialOptions = item.financialOptions && item.financialOptions.length > 0;
    const hasBranch = item.branches && item.branches.length > 0;
    const hasConnectedBranches =
      hasBranch &&
      item.branches?.[0].connectedBranches &&
      item.branches[0].connectedBranches.length > 0;
    const connectedBranch = !!hasConnectedBranches
      ? item.branches?.[0].connectedBranches.filter((x) => x.id === retailer.id)[0]
      : undefined;
    const hasConnectedFinancialOptions =
      connectedBranch &&
      connectedBranch.financialOptions &&
      connectedBranch.financialOptions.length > 0;

    if (hasConnectedFinancialOptions) {
      actions.getDefaultFinancialInformation(id, retailer.id);
    } else if (hasFinancialOptions && hasBranch && item.branches?.[0]?.id) {
      actions.getDefaultFinancialInformation(id, item.branches?.[0]?.id);
    } else {
      actions.resetDefaultFinancialInformation();
    }

    actions.resetCurrentFinancialInformation();
  }, [id, item, retailer]);

  useEffect(() => {
    return () => {
      if (liveshoppingReferenceRef.current) {
        actions.clearLiveshoppingReference();
      }
      actions.resetDefaultFinancialInformation();
      actions.resetCurrentFinancialInformation();
    };
  }, []);

  useEffect(() => {
    if (item) {
      document.title = text.createPageTitle(
        item.registrationNumber,
        item.title,
        item.shortDescription,
      );
      const spreadable: {
        liveshoppingReference?: string;
      } = {};
      if (liveshoppingReferenceRef.current) {
        spreadable.liveshoppingReference = liveshoppingReferenceRef.current;
      }

      if (item.id === id) {
        trackPageViewEvent(item, spreadable);
      }
    } else {
      document.title = text.createPageTitle('Laddar...');
    }
  }, [item]);

  useEffect(() => {
    events.resetViewport();

    if (!item || (item && item.id !== id)) {
      actions.find(id);
      actions.clearRetailer();
    } else if (!process.env.SSR && item) {
      const spreadable: {
        liveshoppingReference?: string;
      } = {};
      if (liveshoppingReferenceRef.current) {
        spreadable.liveshoppingReference = liveshoppingReferenceRef.current;
      }

      actions.trackViewEvent(item, spreadable);
      onGetRetailer();
    }
  }, [id, item]);

  if (notFound) return <MissingItemView id={id} />;

  if (!item)
    return (
      <Page>
        <Loader />
      </Page>
    );

  const fuelTypesElectric = marketCode === 'NO' ? 'Elektrisitet' : 'El';

  return (
    <>
      {showMessageModal && <MessageModal />}
      <FloatingHeader visible={showFloatingHeader} />
      <div className="container">
        <div data-wx-itemcontent="">
          <div className="item-content-main">
            <Gallery />
            <Information />
            <Card id={id} contactSectionRef={contactSectionRef} />
            <SafetyData item={item} />
            <GuaranteePackage />
            <Properties />
            <Equipment />
            {item._id === id && item.fuelTypes === fuelTypesElectric && (
              <ChargingCost id={id} item={item} />
            )}
            <Documents />
          </div>
          <div className="item-section item-section-stretch hidden-lt-md">
            <Separator />
          </div>
          <Accessories accessories={item.accessories} />
          <Promos />
          {!!retailer && <Branch />}
          <RenderClientSide skeleton={<Loader />}>
            <>
              {!!retailer && (
                <div className="item-section item-section-stretch hidden-lt-md">
                  <Separator />
                </div>
              )}
              <div className="item-section item-section-full-width">
                <SelectedArticle modelSeries={item.modelSeries} />
              </div>
              <div className="item-section item-section-full-width">
                <VehicleList
                  source={{
                    type: 'relatedFrom',
                    value: id,
                  }}
                  heading={t('similarVehicles', { ns: 'item' })}
                />
              </div>
              <ArticleList
                heading={t('relatedArticles', { ns: 'article' })}
                actionLabel={t('allArticles', { ns: 'article' })}
                actionLink={`/${resolveArticlesRoute()}`}
                query={`${item.manufacturer} ${item.modelSeries}`}
              />
            </>
          </RenderClientSide>
        </div>
      </div>
    </>
  );
};

export { Item as ItemComponent };

export default Item;
